import outputCatalog from "./mobbiOutputSettings.json";
import mobbiVideoCatalog from "./mobbiVideoModels.json";
import mobbiImageCatalog from "./mobbiImageModels.json";
import mobbiExtraCatalog from "./mobbiExtraModels.json";
import mobbiEffectCatalog from "./mobbiVideoEffects.json";
import { klingEffectSceneFor } from "./creativeDirectEffects";
import creativeVoiceCatalog from "./creativeVoices.json";

/**
 * Creative tools are sold as quality tiers (Fast / Standard / Best), not as
 * a model catalog. Model ids are internal routing detail: the tier layer maps
 * to whichever engine we currently run, so swapping an engine is a config
 * change and the page never names a third-party model (Sep 21 2026: the
 * "Advanced: choose a specific engine" picker was removed for that reason).
 */
export type MobbiImageModel = {
  company: string;
  perSecond?: boolean;
  /** Billed per started minute of the uploaded clip (dubbing). */
  perMinute?: boolean;
  /** Billed per started 1,000 characters of the prompt (speech). */
  perKChars?: boolean;
  clipLimits?: { min: number; max: number; maxMB: number };
  name: string;
  credits: number;
  type: string;
  model: string;
  multiple: boolean;
  defaults: Record<string, unknown>;
  shapeField?: string;
  shapes: Record<string, string>;
  /** What the provider returns; defaults from the tool category. */
  output?: "image" | "video" | "audio" | "model";
  /** Video-to-video models that also take a source image (motion transfer). */
  needsImage?: boolean;
  /** Image-to-video models whose second image is the end frame. */
  endFrame?: boolean;
  /** Models that take reference images instead of a single source. */
  references?: boolean;
  /** Mobbi video effect (the model id is the effect id). */
  effect?: boolean;
  duration?: number;
  aspectRatio?: string | null;
  category?: string;
  cover?: string | null;
  engine?: string;
  voiceProvider?: "elevenlabs" | "minimax";
};
export const mobbiImageModels: Record<string, MobbiImageModel> =
  mobbiImageCatalog;
export const mobbiVideoModels: Record<string, MobbiImageModel> =
  mobbiVideoCatalog;
export const mobbiExtraModels: Record<string, MobbiImageModel> =
  mobbiExtraCatalog as Record<string, MobbiImageModel>;
export const mobbiEffectModels: Record<string, MobbiImageModel> =
  mobbiEffectCatalog as Record<string, MobbiImageModel>;
export const creativeGatewayModels: Record<string, MobbiImageModel> = {
  ...mobbiImageModels,
  ...mobbiVideoModels,
  ...mobbiExtraModels,
  ...mobbiEffectModels,
};
export type CreativeVoice = {
  voiceId: string;
  name: string;
  provider: "elevenlabs" | "minimax";
  gender: string;
  language: string;
  style: string;
};
export const creativeVoices: CreativeVoice[] =
  creativeVoiceCatalog as CreativeVoice[];
export const mobbiTextVideoModels = Object.keys(mobbiVideoModels).filter(
  (id) => mobbiVideoModels[id].type === "text-to-video",
);
export const mobbiImageVideoModels = Object.keys(mobbiVideoModels).filter(
  (id) => mobbiVideoModels[id].type === "image-to-video",
);
export const mobbiVideoEditModels = [
  "rf-edit-wan-2.7-videoedit",
  "rf-edit-happyhorse-1.0-video-edit",
  "lucy-edit",
  "rf-edit-gen4-aleph",
  "rf-edit-grok-imagine-video-edit",
];
const extraIds = (prefix: string) =>
  Object.keys(mobbiExtraModels).filter((id) => id.startsWith(prefix));
export const mobbiVideoExtendModels = extraIds("rf-ext-");
export const mobbiMimicModels = extraIds("rf-mimic-");
export const mobbiTransitionModels = extraIds("rf-tr-");
export const mobbiReferenceVideoModels = extraIds("rf-r2v-");
export const mobbiSpeechModels = extraIds("rf-tts-");
export const mobbiDubbingModels = extraIds("rf-dub-");
/** Effect families that fit performers and music content; the rest stay in the snapshot unused. */
export const creativeEffectCategories = [
  "AI Dance",
  "Fun",
  "Style Lab",
  "Shot Language",
  "Super Power",
  "Morph Me",
  "Viral Presets",
  "Quirk Lab",
];
// The full effect catalog renders on Renderful; while Renderful is refused only
// the effects WaveSpeed's Kling effects can render (klingEffectSceneFor) stay
// available — the server's availability list tells the client which.
export const mobbiVideoEffectIds = Object.keys(mobbiEffectModels).filter((id) =>
  creativeEffectCategories.includes(mobbiEffectModels[id].category || ""),
);
/** Effects that still render when Renderful is unavailable (WaveSpeed Kling effects). */
export const creativeEffectHasBackup = (id: string) =>
  Boolean(klingEffectSceneFor(mobbiEffectModels[id]?.model || ""));
export const creativeClipLimits = (model: string) =>
  creativeGatewayModels[model]?.clipLimits || { min: 1, max: 15, maxMB: 20 };
const mobbiGenerationModels = Object.keys(mobbiImageModels).filter(
  (id) => mobbiImageModels[id].type === "text-to-image",
);
const mobbiEditModels = Object.keys(mobbiImageModels).filter(
  (id) => mobbiImageModels[id].type === "image-to-image",
);
/** The subset Mobbi's Image Editor offers for AI edits. */
const mobbiImageEditorModels = [
  "rf-edit-flux-2",
  "rf-edit-flux-2-flex",
  "rf-edit-flux-kontext-pro",
  "rf-edit-flux-kontext-max",
  "rf-edit-seedream-4",
  "rf-edit-nano-banana-pro",
].filter((id) => mobbiImageModels[id]);
/** Prompted photo presets use the strongest instruction-following editors. */
const photoPresetModels = [
  "rf-edit-nano-banana-pro",
  "rf-edit-flux-kontext-pro",
  "rf-edit-gpt-image-2",
  "seedream",
].filter((id) => id === "seedream" || mobbiImageModels[id]);
export function creativeAspectRatios(
  model: string,
  category: CreativeCategory,
) {
  const spec = creativeGatewayModels[model];
  return spec
    ? Object.keys(spec.shapes)
    : category === "image"
      ? ["1:1", "16:9", "9:16"]
      : category === "video"
        ? ["16:9", "9:16"]
        : [];
}

/**
 * Video engines we call directly (BytePlus ModelArk) instead of through the
 * Renderful gateway, billed per second of the duration the user picks.
 *
 * Sep 21 2026 — Seedance 2.0 Fast. Through the gateway it cost us
 * $0.825–1.65 a clip and was priced at 189–297 credits for a fixed 8 s.
 * Direct it is token-metered: a real 4 s 480p job used 40,594 tokens at
 * $5.60 / M (list) ≈ $0.057/s, so 720p ≈ $0.126/s. At ~$0.0175 a credit and
 * the ~2x markup the other tiers carry that was 15 credits per second.
 * Lowered to 12 the same day (Luv): our bill shows BytePlus discounts us —
 * a 10% account discount on everything, and the limited-time Seedance promo
 * (Mini is billed at exactly 60% off, so the "enterprise" promo applies to
 * us; Fast's is 25% until Oct 7 2026). Expected cost ≈ $0.095/s until then,
 * ≈ $0.113/s after, so 12 credits (≈ $0.21) is ~2.2x now and ~1.9x later.
 * BytePlus refuses input images with real human faces; those jobs are
 * resubmitted on `fallbackEndpoint` (WaveSpeed hosts the same model and
 * accepts them) — see creativeToolJobs.
 */
export type CreativeDirectVideoModel = {
  provider: "byteplus";
  /** ModelArk model id. */
  providerModel: string;
  takesImage: boolean;
  resolution: "480p" | "720p";
  durations: number[];
  defaultDuration: number;
  creditsPerSecond: number;
  /** WaveSpeed endpoint that runs the same engine when BytePlus refuses the image. */
  fallbackEndpoint: string;
};
export const creativeDirectVideoModels: Record<string, CreativeDirectVideoModel> = {
  "seedance-fast-t2v": {
    provider: "byteplus",
    providerModel: "dreamina-seedance-2-0-fast-260128",
    takesImage: false,
    resolution: "720p",
    durations: [5, 8, 10, 15],
    defaultDuration: 5,
    creditsPerSecond: 12,
    fallbackEndpoint: "bytedance/seedance-2.0-fast/text-to-video",
  },
  "seedance-fast-i2v": {
    provider: "byteplus",
    providerModel: "dreamina-seedance-2-0-fast-260128",
    takesImage: true,
    resolution: "720p",
    durations: [5, 8, 10, 15],
    defaultDuration: 5,
    creditsPerSecond: 12,
    fallbackEndpoint: "bytedance/seedance-2.0-fast/image-to-video",
  },
};
/** Seconds the user picked for a direct video engine (its default when unset or invalid). */
export function creativeDirectDuration(
  model: string,
  options: Record<string, string> = {},
) {
  const spec = creativeDirectVideoModels[model];
  if (!spec) return undefined;
  const picked = Number(options.duration);
  return spec.durations.includes(picked) ? picked : spec.defaultDuration;
}

/** Feature names are Solmi workflows; model names describe actual integrations. */
export type CreativeField = "image" | "reference" | "mask" | "video";
export type CreativeCategory = "image" | "video" | "audio";
export type CreativeTierId = "fast" | "standard" | "best";
export type CreativeTier = {
  id: CreativeTierId;
  label: string;
  /** Internal engine behind the tier; never shown on the card. */
  model: string;
  description: string;
};
export type CreativeOption = {
  key: string;
  label: string;
  /** Request body key the choice is sent under. */
  payloadKey: string;
  choices: { value: string; label: string }[];
  default?: string;
  /** The choice is a prompt fragment, not a provider parameter. */
  promptPrefix?: boolean;
};
export type CreativeTool = {
  id: string;
  name: string;
  description: string;
  category: CreativeCategory;
  fields: CreativeField[];
  /** Every engine the server accepts for this tool (tiers plus advanced choices). */
  models: string[];
  /** Quality tiers shown on the card; absent for single-engine utilities and effects. */
  tiers?: CreativeTier[];
  prompt?: string;
  href?: string;
  /** Per-tool wording for upload fields. */
  labels?: Partial<Record<CreativeField, string>>;
  options?: CreativeOption[];
  /** Prompt is optional (a preset or the model supplies it). */
  optionalPrompt?: boolean;
  /** Show the client-side adjustment panel before upload (image editor). */
  adjustments?: boolean;
  /** The picker is a catalog of named presets (effects), not engines. */
  presetPicker?: boolean;
};
export const creativeModels: Record<
  string,
  { name: string; credits: number; company: string }
> = {
  ...creativeGatewayModels,
  "z-image": { name: "Z-Image Turbo", credits: 3, company: "Alibaba" },
  seedream: { name: "Seedream 5.0 Lite", credits: 9, company: "ByteDance" },
  inpaint: { name: "Z-Image Inpaint", credits: 5, company: "Alibaba" },
  "image-upscale": { name: "Image Upscaler", credits: 5, company: "WaveSpeed" },
  "image-face-swap": {
    name: "Image Face Swap",
    credits: 12,
    company: "WaveSpeed",
  },
  "background-remove": {
    name: "Background Remover",
    credits: 5,
    company: "WaveSpeed",
  },
  "sora-2": { name: "Sora 2", credits: 90, company: "OpenAI" },
  // `credits` is the per-second rate; see creativeDirectVideoModels.
  "seedance-fast-t2v": { name: "Seedance 2.0 Fast", credits: 12, company: "ByteDance" },
  "seedance-fast-i2v": { name: "Seedance 2.0 Fast", credits: 12, company: "ByteDance" },
  "sora-2-pro": { name: "Sora 2 Pro", credits: 270, company: "OpenAI" },
  "lucy-edit": { name: "Lucy Edit", credits: 16, company: "Decart" },
  "video-upscale": { name: "Video Upscaler", credits: 3, company: "WaveSpeed" },
  "video-face-swap": {
    name: "Video Face Swap",
    credits: 5,
    company: "WaveSpeed",
  },
};
const legacyCreativeTools: CreativeTool[] = [
  {
    id: "ai-image-generator",
    name: "AI Image Generator",
    description: "Create artwork, covers, and stills from a prompt.",
    category: "image",
    fields: [],
    models: ["seedream", "z-image", ...mobbiGenerationModels],
  },
  {
    id: "relight",
    name: "Relight",
    description: "Change the lighting, color, and atmosphere of a photo.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt:
      "Relight this image. Preserve the subject and composition. Lighting direction: ",
  },
  {
    id: "relight-lighting",
    name: "Relight Lighting",
    description: "Direct the light position and color with a prompt.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt: "Adjust only the lighting position and color in this image: ",
  },
  {
    id: "inpaint",
    name: "Inpaint",
    description: "Paint an area and describe what should replace it.",
    category: "image",
    fields: ["image", "mask"],
    models: ["inpaint"],
  },
  {
    id: "moodboard",
    name: "Moodboard",
    description: "Combine two visual references into a cohesive moodboard.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Create a cohesive editorial moodboard using both reference images. Direction: ",
  },
  {
    id: "character-creator",
    name: "Character Creator",
    description: "Design a character with a consistent reference sheet.",
    category: "image",
    fields: [],
    models: ["seedream"],
    prompt:
      "Create a consistent character reference sheet with front, side, and full-body views: ",
  },
  {
    id: "image-character-swap",
    name: "Character Swap",
    description: "Replace the subject using a character reference.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Replace the character in the first image with the character in the second. Preserve the first image's composition and environment. ",
  },
  {
    id: "image-upscaler",
    name: "Image Upscaler",
    description: "Enhance an image to 4K resolution.",
    category: "image",
    fields: ["image"],
    models: ["image-upscale"],
  },
  {
    id: "face-swap",
    name: "Face Swap",
    description: "Transfer a reference face into your photo.",
    category: "image",
    fields: ["image", "reference"],
    models: ["image-face-swap"],
  },
  {
    id: "draw-to-edit",
    name: "Draw to Edit",
    description: "Upload a sketch and turn it into a finished image.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt:
      "Turn this sketch into a finished image, following its composition. ",
  },
  {
    id: "fashion-factory",
    name: "Fashion Factory",
    description: "Create a fashion set from a model and outfit reference.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Create an editorial fashion set. Dress the model in image one using the outfit in image two. Preserve the model's identity. ",
  },
  {
    id: "image-enhancer",
    name: "Image Enhancer",
    description: "Sharpen, denoise, and enlarge a photo to 4K.",
    category: "image",
    fields: ["image"],
    models: ["image-upscale"],
  },
  {
    id: "hairstyle-changer",
    name: "AI Hairstyle Changer",
    description: "Try a new haircut or color on your photo.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    prompt:
      "Change only the hairstyle of the person in this photo. Keep the face, skin, clothing, background, and lighting exactly the same. New hairstyle: ",
  },
  {
    id: "ai-video-generator",
    name: "AI Video Generator",
    description:
      "Generate a cinematic clip from text with your choice of video model.",
    category: "video",
    fields: [],
    models: ["sora-2", "sora-2-pro"],
  },
  {
    id: "lyrics-video",
    name: "Lyrics Video",
    description: "Turn a song into a synced lyric video.",
    category: "video",
    fields: [],
    models: [],
    href: "/tools/lyric-video-generator",
  },
  {
    id: "lip-sync-video",
    name: "Lip Sync Video",
    description: "Sync a portrait to your song or audio.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-singing-video-generator",
  },
  {
    id: "mixed-media",
    name: "Mixed Media",
    description: "Build a music video with references and a storyboard.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-music-video-generator",
  },
  {
    id: "asmr",
    name: "ASMR",
    description:
      "An ASMR prompt preset for close-up video with generated audio.",
    category: "video",
    fields: [],
    models: ["sora-2", "sora-2-pro"],
    prompt:
      "Create an ASMR video with detailed close-up visuals and synchronized, delicate sensory sounds. No music or speech. ",
  },
  {
    id: "modify-videos",
    name: "Modify Videos",
    description: "Edit a short clip using natural-language instructions.",
    category: "video",
    fields: ["video"],
    models: ["lucy-edit"],
  },
  {
    id: "lip-sync",
    name: "Lip Sync",
    description: "Create a talking or singing portrait from audio.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-singing-video-generator",
  },
  {
    id: "draw-to-video",
    name: "Draw to Video",
    description: "Animate your uploaded drawing into a short clip.",
    category: "video",
    fields: ["image"],
    models: ["sora-2", "sora-2-pro"],
  },
  {
    id: "sketch-to-video",
    name: "Sketch to Video",
    description: "Bring a sketch to life with image-to-video models.",
    category: "video",
    fields: ["image"],
    models: ["sora-2", "sora-2-pro"],
  },
  {
    id: "video-upscaler",
    name: "Video Upscaler",
    description: "Enhance a short video to 1080p.",
    category: "video",
    fields: ["video"],
    models: ["video-upscale"],
  },
  {
    id: "video-character-swap",
    name: "Character Face Swap",
    description: "Replace a face in a clip using a portrait reference.",
    category: "video",
    fields: ["video", "reference"],
    models: ["video-face-swap"],
  },
  {
    id: "marketing-studio",
    name: "Marketing Studio",
    description: "Turn a product photo into a UGC, demo, or cinematic ad.",
    category: "video",
    fields: ["image"],
    models: ["rf-i2v-grok-imagine-video", "rf-i2v-seedance-2.0-mini", "rf-i2v-kling-v2-6"],
  },
];
const LANGUAGE_CHOICES = [
  ["en", "English"],
  ["zh", "Chinese"],
  ["fr", "French"],
  ["hi", "Hindi"],
  ["it", "Italian"],
  ["ja", "Japanese"],
  ["ko", "Korean"],
  ["pt", "Portuguese"],
  ["ru", "Russian"],
  ["tr", "Turkish"],
  ["es", "Spanish"],
  ["de", "German"],
  ["ar", "Arabic"],
  ["pl", "Polish"],
  ["id", "Indonesian"],
  ["fil", "Filipino"],
].map(([value, label]) => ({ value, label }));
/** Mobbi's Marketing Studio ad styles, as prompt prefixes for image-to-video. */
export const creativeAdStyles: { value: string; label: string; prompt: string }[] =
  [
    {
      value: "ugc",
      label: "UGC ad",
      prompt:
        "Create a UGC-style product advertisement. A person casually presents the product in the image to the camera, talking enthusiastically about its benefits. Natural, authentic setting like a bedroom or living room, handheld camera feel, natural lighting. They hold up the product, show it from different angles, and share why they love it. ",
    },
    {
      value: "how-to-demo",
      label: "How-to demo",
      prompt:
        "Create a tutorial-style video for the product in the image. A person demonstrates step-by-step how to use it with clear, well-lit close-up shots, briefly explaining each step. Educational and helpful tone. ",
    },
    {
      value: "unboxing",
      label: "Unboxing",
      prompt:
        "Create an unboxing video for the product in the image. A person excitedly opens a package revealing the product: close-ups of the packaging opening, tissue paper pulled aside, the product lifted out, genuine excitement. Natural lighting, overhead and eye-level angles. ",
    },
    {
      value: "product-highlight",
      label: "Product highlight",
      prompt:
        "Create a product highlight video for the product in the image. Dynamic, fast-paced motion graphics from dramatic angles: smooth camera moves, the product spinning and floating, particles and light effects. Energetic and eye-catching, no person, premium 3D-style visualization. ",
    },
    {
      value: "product-review",
      label: "Product review",
      prompt:
        "Create a product review video for the product in the image. A person genuinely reacts to trying it for the first time, examines it closely, tests it, and shares honest impressions. Close-up reaction shots, natural expressions, authentic customer-review tone. ",
    },
    {
      value: "cinematic-ad",
      label: "Cinematic ad",
      prompt:
        "Create a cinematic TV commercial for the product in the image. Professional lighting, dramatic camera angles, slow-motion shots, high production value with smooth transitions, music building to a crescendo, ending on a product hero shot. ",
    },
    {
      value: "ugc-virtual-try-on",
      label: "UGC try-on",
      prompt:
        "Create a UGC-style virtual try-on video for the product in the image. A person casually tries on or applies it in a selfie-style video with mirror-style framing and natural lighting, admires the result, and shows it off with a smile. ",
    },
    {
      value: "pro-virtual-try-on",
      label: "Professional try-on",
      prompt:
        "Create a professional virtual try-on video for the product in the image. Studio lighting, multiple angles showing the product on a model, clean background, smooth transitions between poses. Polished editorial fashion feel. ",
    },
  ];
const voiceChoices = (provider: "elevenlabs" | "minimax") =>
  creativeVoices
    .filter((v) => v.provider === provider)
    .map((v) => ({
      value: v.voiceId,
      label: `${v.name} · ${v.style}`,
    }));
export const creativeVoiceOptions: Record<string, CreativeOption> = {
  elevenlabs: {
    key: "voice",
    label: "Voice",
    payloadKey: "voice_id",
    choices: voiceChoices("elevenlabs"),
    default: "JBFqnCBsd6RMkjVDRZzb",
  },
  minimax: {
    key: "voice",
    label: "Voice",
    payloadKey: "voice_id",
    choices: voiceChoices("minimax"),
    default: "Wise_Woman",
  },
};
const tiers = (
  ...entries: [CreativeTierId, string, string][]
): CreativeTier[] =>
  entries
    .filter(([, model]) => creativeModels[model])
    .map(([id, model, description]) => ({
      id,
      model,
      description,
      label: id === "fast" ? "Fast" : id === "standard" ? "Standard" : "Best",
    }));
const imageTiers = tiers(
  ["fast", "z-image", "Quick drafts and thumbnails"],
  ["standard", "seedream", "Cover art and stills, 2K"],
  ["best", "rf-image-nano-banana-pro", "Sharpest detail and text, 4K-ready"],
);
const editTiers = tiers(
  ["standard", "seedream", "Faithful edits, keeps the subject"],
  ["best", "rf-edit-nano-banana-pro", "Best instruction following"],
);
// Sep 21 2026: Standard was Kling 2.6 and Best was Veo 3.1 Fast. In the first
// 16 h live every Kling job (7/7) and the one Veo job were rejected by the
// provider within ~11 s ("Generation failed", refunded), while PixVerse V6
// finished 21 of 23 — so two of three tiers on the two main video tools were
// dead buttons, and Standard is the default. Luv: Standard = PixVerse V6,
// Best = Seedance 2.0 Fast, no Fast tier. Put Kling/Veo back only after a
// live call has actually succeeded.
const videoTiers = tiers(
  ["standard", "rf-t2v-pixverse-v6", "Quick 5-second clips"],
  ["best", "seedance-fast-t2v", "Cinematic motion, follows the prompt closely"],
);
const imageVideoTiers = tiers(
  ["standard", "rf-i2v-pixverse-v6", "Quick 5-second clips"],
  ["best", "seedance-fast-i2v", "Natural motion, keeps the subject"],
);
const videoEditTiers = tiers(
  ["fast", "rf-edit-grok-imagine-video-edit", "Quick style and object edits"],
  ["standard", "rf-edit-happyhorse-1.0-video-edit", "Balanced quality, up to 15s"],
  ["best", "rf-edit-wan-2.7-videoedit", "Highest fidelity, up to 10s"],
);
const promoTiers = tiers(
  ["standard", "rf-i2v-grok-imagine-video", "Fast social ads"],
  ["best", "rf-i2v-seedance-2.0-mini", "Follows the brief closely"],
);
/** Public catalog: the workflows a musician needs around a music video. Legacy presets remain only for existing jobs. */
export const creativeTools: CreativeTool[] = [
  {
    id: "ai-image-generator",
    name: "AI Image Generator",
    description: "Cover art, characters, and stills from a prompt.",
    category: "image",
    fields: [],
    models: ["seedream", "z-image", ...mobbiGenerationModels],
    tiers: imageTiers,
  },
  {
    id: "image-to-image",
    name: "Image to Image",
    description: "Transform a photo or artwork with a prompt.",
    category: "image",
    fields: ["image"],
    models: ["seedream", ...mobbiEditModels],
    tiers: editTiers,
  },
  {
    id: "image-editor",
    name: "Image Editor",
    description:
      "Adjust light and color, crop or rotate, then describe an AI edit.",
    category: "image",
    fields: ["image"],
    models: ["seedream", ...mobbiImageEditorModels],
    tiers: editTiers,
    adjustments: true,
    optionalPrompt: true,
    prompt: "Edit this image. ",
  },
  ...legacyCreativeTools.filter((t) => t.id === "inpaint"),
  {
    id: "image-eraser",
    name: "Image Eraser",
    description: "Paint over an object and remove it cleanly.",
    category: "image",
    fields: ["image", "mask"],
    models: ["inpaint"],
    optionalPrompt: true,
    prompt:
      "Remove everything in the masked area and fill it with matching background, seamlessly. ",
  },
  {
    id: "background-remover",
    name: "Background Remover",
    description: "Cut out the subject with a transparent background.",
    category: "image",
    fields: ["image"],
    models: ["background-remove"],
  },
  ...legacyCreativeTools.filter((t) => t.id === "image-upscaler"),
  {
    id: "photo-restoration",
    name: "Photo Restoration",
    description: "Repair scratches, fading, and blur in an old photo.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    tiers: editTiers,
    optionalPrompt: true,
    prompt:
      "Restore this old photograph: remove scratches, dust, creases, and fading, recover natural colors and sharp detail, keep every face and feature faithful to the original. ",
  },
  {
    id: "photo-to-cartoon",
    name: "Photo to Cartoon",
    description: "Turn a photo into a cartoon or anime avatar.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    tiers: editTiers,
    optionalPrompt: true,
    options: [
      {
        key: "style",
        label: "Cartoon style",
        payloadKey: "style",
        promptPrefix: true,
        choices: [
          { value: "Pixar-style 3D cartoon", label: "3D cartoon" },
          { value: "Japanese anime", label: "Anime" },
          { value: "Studio Ghibli style", label: "Ghibli" },
          { value: "classic Disney 2D animation", label: "Disney 2D" },
          { value: "comic book ink and halftone", label: "Comic" },
          { value: "pixel art", label: "Pixel art" },
        ],
        default: "Pixar-style 3D cartoon",
      },
    ],
    prompt:
      "Redraw this photo as a {style} illustration. Keep the same person, pose, expression, and composition. ",
  },
  ...legacyCreativeTools.filter((t) => t.id === "face-swap"),
  {
    id: "ai-video-generator",
    name: "AI Video Generator",
    description: "A cinematic clip from text for B-roll and teasers.",
    category: "video",
    fields: [],
    models: ["seedance-fast-t2v", ...mobbiTextVideoModels],
    tiers: videoTiers,
  },
  {
    id: "image-to-video",
    name: "Image to Video",
    description: "Animate a photo, illustration, or sketch into a video.",
    category: "video",
    fields: ["image"],
    models: ["seedance-fast-i2v", ...mobbiImageVideoModels],
    tiers: imageVideoTiers,
  },
  {
    id: "transition-video",
    name: "Transition",
    description: "Morph from a start frame to an end frame.",
    category: "video",
    fields: ["image", "reference"],
    models: mobbiTransitionModels,
    tiers: tiers(
      ["standard", "rf-tr-pixverse-v6-transition", "Smooth 5-second morph"],
      ["best", "rf-tr-pixverse-c1-transition", "Higher fidelity morph"],
    ),
    labels: { image: "Start frame", reference: "End frame" },
    optionalPrompt: true,
  },
  {
    id: "reference-to-video",
    name: "Reference to Video",
    description:
      "Keep your character or artwork consistent using reference images.",
    category: "video",
    fields: ["image", "reference"],
    // Best was Kling O1 reference-to-video; every Kling route failed live
    // (see videoTiers). The provider has no Seedance 2.0 Fast *reference*
    // model, only t2v / i2v, so Best runs the i2v engine led by reference
    // image 1 — reference image 2 only applies on Standard (PixVerse C1).
    models: [...mobbiReferenceVideoModels, "seedance-fast-i2v"],
    tiers: tiers(
      ["standard", "rf-r2v-pixverse-c1-r2v", "Up to 2 references"],
      ["best", "seedance-fast-i2v", "Uses first reference"],
    ),
    labels: {
      image: "Reference image 1",
      reference: "Reference image 2 (optional)",
    },
  },
  {
    id: "video-effects",
    name: "Video Effects",
    description:
      "One-tap effects for performers: dances, morphs, style looks, and stunts.",
    category: "video",
    fields: ["image"],
    models: mobbiVideoEffectIds,
    presetPicker: true,
    optionalPrompt: true,
  },
  {
    id: "video-extender",
    name: "Video Extender",
    description: "Continue a clip for a few more seconds.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoExtendModels,
    tiers: tiers(
      ["fast", "rf-ext-pixverse-v6-extend", "Quick continuation"],
      ["standard", "rf-ext-seedance-2.0-mini-video-extend", "Keeps motion and sound"],
      ["best", "rf-ext-seedance-2.0-video-extend", "Highest fidelity"],
    ),
    optionalPrompt: true,
  },
  ...legacyCreativeTools
    .filter((t) => t.id === "modify-videos")
    .map((t) => ({
      ...t,
      name: "Video Editor",
      models: mobbiVideoEditModels,
      tiers: videoEditTiers,
    })),
  {
    id: "video-background-remover",
    name: "Video Background Remover",
    description: "Replace or clear the background behind the subject.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoEditModels.filter((id) => id !== "rf-edit-gen4-aleph"),
    tiers: videoEditTiers,
    optionalPrompt: true,
    prompt:
      "Remove the background of this video and keep the main subject exactly as is. New background: ",
  },
  {
    id: "video-eraser",
    name: "Video Eraser",
    description: "Describe an object or person to remove from a clip.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoEditModels.filter((id) => id !== "rf-edit-gen4-aleph"),
    tiers: videoEditTiers,
    prompt:
      "Remove the following from every frame of this video and fill the space naturally, keeping everything else unchanged: ",
  },
  {
    id: "mimic",
    name: "Motion Transfer",
    description: "Make a photo copy the moves from a reference video.",
    category: "video",
    fields: ["image", "video"],
    // Single engine, no tier picker (Luv, Sep 21 2026): Wan 2.2 Animate.
    // Kling 2.6 Motion Control was the Standard tier and is dropped with the
    // other Kling routes.
    models: ["rf-mimic-wan-2.2-animate"],
    labels: { image: "Character photo", video: "Motion video (2–15 seconds)" },
    optionalPrompt: true,
    options: [
      {
        key: "orientation",
        label: "Follow framing of",
        payloadKey: "character_orientation",
        choices: [
          { value: "video", label: "The motion video" },
          { value: "image", label: "The character photo" },
        ],
        default: "video",
      },
    ],
  },
  {
    id: "song-promo-ad",
    name: "Song Promo Ad",
    description: "Turn cover art or merch into a UGC, demo, or cinematic ad.",
    category: "video",
    fields: ["image"],
    models: promoTiers.map((t) => t.model),
    tiers: promoTiers,
    labels: { image: "Cover art, merch, or product photo" },
    optionalPrompt: true,
    options: [
      {
        key: "adStyle",
        label: "Ad style",
        payloadKey: "adStyle",
        promptPrefix: true,
        choices: creativeAdStyles.map(({ value, label }) => ({ value, label })),
        default: "ugc",
      },
    ],
    prompt: "{adStyle}",
  },
  ...legacyCreativeTools.filter((t) => t.id === "video-upscaler"),
  ...legacyCreativeTools.filter((t) => t.id === "lip-sync"),
  ...legacyCreativeTools
    .filter((t) => t.id === "video-character-swap")
    .map((t) => ({ ...t, name: "Video Face Swap" })),
  {
    id: "voiceover",
    name: "AI Voiceover",
    description: "Spoken intros, ad reads, and narration in 70+ languages.",
    category: "audio",
    fields: [],
    models: mobbiSpeechModels,
    tiers: tiers(
      ["fast", "rf-tts-eleven_flash_v2_5", "Clear and quick"],
      ["standard", "rf-tts-speech-02-hd", "Warm, natural delivery"],
      ["best", "rf-tts-eleven_v3", "Most expressive"],
    ),
  },
  {
    id: "video-translate",
    name: "Video Translate",
    description: "Dub a clip into another language, keeping the voice.",
    category: "audio",
    fields: ["video"],
    models: mobbiDubbingModels,
    labels: { video: "Video or audio clip (up to 3 minutes)" },
    optionalPrompt: true,
    options: [
      {
        key: "targetLang",
        label: "Translate to",
        payloadKey: "target_lang",
        choices: LANGUAGE_CHOICES,
        default: "es",
      },
    ],
  },
];
export const findCreativeToolForJob = (id: string) =>
  creativeTools.find((t) => t.id === id) ||
  legacyCreativeTools.find((t) => t.id === id);
/**
 * Public URL slug per tool id, where the slug differs from the id. The id is
 * the contract with the jobs API, stored jobs, pricing and the server
 * allowlist, so it never changes; the slug is only what appears in the URL and
 * is worded for search ("ai video editor", "motion transfer", "ai voiceover").
 * The old id-based paths 301 to these (PERMANENT_REDIRECTS in seo.ts).
 */
export const creativeToolSlugs: Record<string, string> = {
  "modify-videos": "ai-video-editor",
  mimic: "motion-transfer",
  voiceover: "ai-voiceover",
};
const creativeToolIdsBySlug: Record<string, string> = Object.fromEntries(
  Object.entries(creativeToolSlugs).map(([id, slug]) => [slug, id]),
);
export const creativeToolSlug = (toolId: string) =>
  creativeToolSlugs[toolId] ?? toolId;
/** Tool id for a URL segment. Accepts the slug and, for old links, the id. */
export const creativeToolIdFromSlug = (segment: string) =>
  creativeToolIdsBySlug[segment] ?? segment;
export const creativeToolPath = (tool: CreativeTool) =>
  tool.href || `/creative-tools/${creativeToolSlug(tool.id)}`;
/**
 * Preset landings: a base tool pre-configured for one musician job (a Spotify
 * Canvas loop, an animated album cover). Each preset is its own URL under
 * /creative-tools/ with its own SEO copy; the studio underneath is the base
 * tool with the aspect ratio, duration and prompt filled in, all editable.
 * Jobs are created and stored under the base tool id, so the API, server
 * allowlist and pricing are untouched.
 */
export type CreativeToolPreset = {
  toolId: string;
  name: string;
  /** Starting aspect ratio; must be one the base tool's engines accept. */
  aspectRatio?: string;
  /** Starting duration in seconds, applied when the engine offers that choice. */
  duration?: number;
  /** Pre-filled, editable prompt. */
  prompt?: string;
};
export const creativeToolPresets: Record<string, CreativeToolPreset> = {
  "spotify-canvas-maker": {
    toolId: "image-to-video",
    name: "Spotify Canvas Maker",
    aspectRatio: "9:16",
    duration: 8,
    prompt:
      "Slow, subtle motion that loops cleanly: gentle camera drift, soft moving light and particles, the artwork stays centered and fully recognizable.",
  },
  "animated-album-cover": {
    toolId: "image-to-video",
    name: "Animated Album Cover Maker",
    aspectRatio: "9:16",
    duration: 5,
    prompt:
      "Bring this album cover to life with subtle, elegant motion: slow parallax, drifting light, hair or fabric moving gently, composition and title unchanged.",
  },
};
/**
 * AI Video Generator is one entry point with three ways in (Luv, Sep 21
 * 2026): from a prompt, from a photo, or from reference images. Each mode is
 * still its own tool id — its own fields, tiers and server validation — so
 * the switch only changes which tool page is showing; the prompt rides along.
 */
export const creativeVideoGeneratorModes: { toolId: string; label: string }[] = [
  { toolId: "ai-video-generator", label: "Text to video" },
  { toolId: "image-to-video", label: "Image to video" },
  { toolId: "reference-to-video", label: "Reference to video" },
];
export const isCreativeVideoGeneratorMode = (toolId: string) =>
  creativeVideoGeneratorModes.some((mode) => mode.toolId === toolId);

/** The engine a tool starts on: its Standard tier, else its first tier, else its first model. */
export const creativeDefaultModel = (tool: CreativeTool) =>
  tool.tiers?.find((t) => t.id === "standard")?.model ||
  tool.tiers?.[0]?.model ||
  tool.models[0];
export const creativeTierFor = (tool: CreativeTool, model: string) =>
  tool.tiers?.find((t) => t.model === model);
export const isPerSecondModel = (model: string) =>
  ["lucy-edit", "video-upscale", "video-face-swap"].includes(model) ||
  Boolean(creativeGatewayModels[model]?.perSecond);
export const isPerMinuteModel = (model: string) =>
  Boolean(creativeGatewayModels[model]?.perMinute);
export const isPerKCharsModel = (model: string) =>
  Boolean(creativeGatewayModels[model]?.perKChars);
export const needsCreativePrompt = (tool: CreativeTool) =>
  !tool.optionalPrompt &&
  ![
    "image-upscaler",
    "background-remover",
    "face-swap",
    "video-upscaler",
    "video-character-swap",
  ].includes(tool.id);
/** Options a tool exposes for a given model (speech voices depend on the model's provider). */
export function creativeToolOptions(tool: CreativeTool, model: string) {
  const output = creativeOutputSettings[model];
  const provider = creativeGatewayModels[model]?.voiceProvider;
  const direct = creativeDirectVideoModels[model];
  return [
    ...(provider ? [creativeVoiceOptions[provider]] : []),
    ...(output?.resolutions.length ? [{ key: "resolution", label: "Resolution", payloadKey: "resolution", choices: output.resolutions.map(value => ({value, label: value.toUpperCase()})), default: output.defaultResolution } satisfies CreativeOption] : []),
    ...(output?.durations.length ? [{ key: "duration", label: "Duration", payloadKey: "duration", choices: output.durations.map(value => ({value: String(value), label: `${value} ${value === 1 ? "second" : "seconds"}`})), default: String(output.defaultDuration) } satisfies CreativeOption] : []),
    ...(direct
      ? [
          {
            key: "duration",
            label: "Duration",
            payloadKey: "duration",
            choices: direct.durations.map((seconds) => ({
              value: String(seconds),
              label: `${seconds} seconds`,
            })),
            default: String(direct.defaultDuration),
          } satisfies CreativeOption,
        ]
      : []),
    ...(tool.options || []),
  ];
}
/** Resolve the tool's prompt prefix with any prompt-fragment options. */
export function creativePromptPrefix(
  tool: CreativeTool,
  options: Record<string, string> = {},
) {
  let prefix = tool.prompt || "";
  for (const option of tool.options || []) {
    if (!option.promptPrefix) continue;
    const value = options[option.key] ?? option.default ?? "";
    const fragment =
      option.key === "adStyle"
        ? creativeAdStyles.find((s) => s.value === value)?.prompt || ""
        : value;
    prefix = prefix.replaceAll(`{${option.key}}`, fragment);
  }
  return prefix;
}
/** Credits for one request given the prompt and (for clip inputs) its duration. */
export function creativeCost(
  model: string,
  {
    prompt = "",
    seconds = 1,
    options = {},
  }: { prompt?: string; seconds?: number; options?: Record<string, string> },
) {
  const direct = creativeDirectVideoModels[model];
  if (direct)
    return direct.creditsPerSecond * creativeDirectDuration(model, options)!;
  const base = creativeOutputPrice(model, options);
  if (isPerSecondModel(model)) return base * Math.max(1, Math.ceil(seconds));
  if (isPerMinuteModel(model)) return base * Math.max(1, Math.ceil(seconds / 60));
  if (isPerKCharsModel(model))
    return base * Math.max(1, Math.ceil(prompt.length / 1000));
  return base;
}
export const creativeOutputKind = (
  tool: CreativeTool | undefined,
  model: string,
): "image" | "video" | "audio" | "model" =>
  creativeGatewayModels[model]?.output ||
  (tool?.category === "audio" ? "audio" : tool?.category || "image");

/** Header dropdowns: audio rides with Video. */
export const creativeMenuCategories: Record<
  "image" | "video",
  CreativeCategory[]
> = { image: ["image"], video: ["video", "audio"] };
export const creativeCategoryLabels: Record<CreativeCategory, string> = {
  image: "Image",
  video: "Video",
  audio: "Audio",
};

/** Preserve catalog order within each company and only include available choices. */
export function groupCreativeModels(models: readonly string[]) {
  const groups = new Map<string, string[]>();
  for (const model of models) {
    const company = creativeModels[model].company;
    const items = groups.get(company) || [];
    items.push(model);
    groups.set(company, items);
  }
  return Array.from(groups, ([company, models]) => ({ company, models }));
}

export const creativeOutputSettings: Record<string, {resolutions: string[]; durations: number[]; defaultResolution: string; defaultDuration: number; prices: Record<string, number>}> = outputCatalog;
export function creativeOutputPrice(model: string, options: Record<string, string> = {}) {
  const config = creativeOutputSettings[model];
  if (!config) return creativeModels[model].credits;
  const resolution = options.resolution ?? config.defaultResolution;
  const duration = options.duration ?? String(config.defaultDuration);
  return config.prices[`${resolution}:${duration}`] ?? creativeModels[model].credits;
}
